import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Button } from '../../ComponnentsUI/common/Button';
import { Select } from '../../ComponnentsUI/common/Select';
import {
  fetchDeliveryFile,
  updateDeliveryFile,
} from '../../../helpers/api/patients/patients';

type AdmissionReason = 'DELIVERY' | 'MAP' | 'RPM' | 'METRORRHAGIA' | 'OTHER';

interface FormData {
  order_number: string | null;
  entry_number: string | null;
  admission_reason: AdmissionReason;
  admission_reason_other: string | null;
  admission_date: string;
  admission_time: string;
  referred: boolean;
  referring_facility: string | null;
  referral_date: string | null;
  referral_reason: string | null;
}

const ADMISSION_REASONS: AdmissionReason[] = [
  'DELIVERY',
  'MAP',
  'RPM',
  'METRORRHAGIA',
  'OTHER',
];

const ADMISSION_REASON_KEYS: Record<AdmissionReason, string> = {
  DELIVERY: 'delivery_admission_reason_delivery',
  MAP: 'delivery_admission_reason_map',
  RPM: 'delivery_admission_reason_rpm',
  METRORRHAGIA: 'delivery_admission_reason_metrorrhagia',
  OTHER: 'delivery_admission_reason_other_value',
};

function dateToInput(d: unknown): string {
  if (d == null) {
    return '';
  }
  if (typeof d === 'string') {
    return d.slice(0, 10);
  }
  return '';
}

function timeToInput(t: unknown): string {
  if (t == null) {
    return '';
  }
  if (typeof t === 'string') {
    return t.slice(0, 5);
  }
  return '';
}

function normalizeTimeForApi(hhmm: string): string {
  if (!hhmm) {
    return '00:00:00';
  }
  if (hhmm.length === 5) {
    return `${hhmm}:00`;
  }
  return hhmm;
}

function mapRecordToForm(src: Record<string, unknown>): FormData {
  return {
    order_number: (src.order_number as string) ?? null,
    entry_number: (src.entry_number as string) ?? null,
    admission_reason: (src.admission_reason as AdmissionReason) || 'DELIVERY',
    admission_reason_other: (src.admission_reason_other as string) ?? null,
    admission_date: dateToInput(src.admission_date),
    admission_time: timeToInput(src.admission_time),
    referred: Boolean(src.referred),
    referring_facility: (src.referring_facility as string) ?? null,
    referral_date: src.referral_date ? dateToInput(src.referral_date) : null,
    referral_reason: (src.referral_reason as string) ?? null,
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  hasWritePermission?: boolean;
}

const DeliveryFileModal: React.FC<Props> = ({
  isOpen,
  onClose,
  patientId,
  hasWritePermission = true,
}) => {
  const { t: tP } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');

  const [data, setData] = useState<FormData | null>(null);
  const [original, setOriginal] = useState<FormData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await fetchDeliveryFile(patientId);
      const src = (res?.data ?? res) as Record<string, unknown> | null;
      if (!src || typeof src !== 'object' || src.id == null) {
        setLoadError(tP('delivery_no_file'));
        setData(null);
        setOriginal(null);
        return;
      }
      const form = mapRecordToForm(src);
      setData(form);
      setOriginal(form);
      setIsEditing(false);
    } catch (e: unknown) {
      const err = e as { status_code?: number; message?: string };
      const msg =
        err?.status_code === 404
          ? tP('delivery_no_active_pregnancy')
          : err?.message || tPM('modals.deliveryFile.errorLoad');
      setLoadError(msg);
      setData(null);
      setOriginal(null);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, tP, tPM]);

  useEffect(() => {
    if (isOpen && patientId) {
      void loadData();
    }
  }, [isOpen, patientId, loadData]);

  const handleSave = async () => {
    if (!data || !original) {
      return;
    }
    setIsSaving(true);
    try {
      const changes: Record<string, unknown> = {};
      const keys: (keyof FormData)[] = [
        'order_number',
        'entry_number',
        'admission_reason',
        'admission_reason_other',
        'admission_date',
        'admission_time',
        'referred',
        'referring_facility',
        'referral_date',
        'referral_reason',
      ];
      for (const key of keys) {
        if (data[key] !== original[key]) {
          if (key === 'admission_time') {
            changes.admission_time = normalizeTimeForApi(data.admission_time);
          } else if (
            key === 'order_number' ||
            key === 'entry_number' ||
            key === 'admission_reason_other' ||
            key === 'referring_facility' ||
            key === 'referral_reason'
          ) {
            changes[key] = data[key] === '' ? null : data[key];
          } else {
            changes[key] = data[key];
          }
        }
      }
      if (Object.keys(changes).length === 0) {
        setIsEditing(false);
        return;
      }
      await updateDeliveryFile(patientId, changes);
      message.success(tPM('modals.deliveryFile.successUpdate'));
      const next = { ...data };
      if (changes.admission_time) {
        next.admission_time = data.admission_time;
      }
      setOriginal(next);
      setIsEditing(false);
    } catch {
      message.error(tPM('modals.deliveryFile.errorUpdate'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (original) {
      setData({ ...original });
    }
    setIsEditing(false);
  };

  const set = <K extends keyof FormData>(key: K, val: FormData[K]) =>
    setData((prev) => (prev ? { ...prev, [key]: val } : prev));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <Modal.Header
        title={tPM('modals.deliveryFile.title')}
        onClose={onClose}
      />
      <Modal.Body className="max-h-[70vh]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : loadError || !data ? (
          <p className="text-sm text-title-page-color-300 dark:text-gray-400 py-6">
            {loadError || tP('delivery_unable_to_load')}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                  {tP('delivery_order_number')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={data.order_number || ''}
                    onChange={(e) =>
                      set('order_number', e.target.value || null)
                    }
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-title-page-color-100 dark:text-white">
                    {data.order_number || '—'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                  {tP('delivery_entry_number')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={data.entry_number || ''}
                    onChange={(e) =>
                      set('entry_number', e.target.value || null)
                    }
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-title-page-color-100 dark:text-white">
                    {data.entry_number || '—'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                {tP('delivery_admission_reason')}
              </label>
              {isEditing ? (
                <Select
                  value={data.admission_reason}
                  onChange={(e) =>
                    set('admission_reason', e.target.value as AdmissionReason)
                  }
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  options={ADMISSION_REASONS.map((r) => ({
                    value: r,
                    label: tP(ADMISSION_REASON_KEYS[r]),
                  }))}
                />
              ) : (
                <p className="text-sm text-title-page-color-100 dark:text-white">
                  {tP(ADMISSION_REASON_KEYS[data.admission_reason])}
                </p>
              )}
            </div>

            {data.admission_reason === 'OTHER' && (
              <div>
                <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                  {tP('delivery_admission_reason_other_label')}
                </label>
                {isEditing ? (
                  <textarea
                    value={data.admission_reason_other || ''}
                    onChange={(e) =>
                      set('admission_reason_other', e.target.value || null)
                    }
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-title-page-color-100 dark:text-white whitespace-pre-wrap">
                    {data.admission_reason_other || '—'}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                  {tP('delivery_admission_date')}
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={data.admission_date}
                    onChange={(e) => set('admission_date', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-title-page-color-100 dark:text-white">
                    {data.admission_date || '—'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                  {tP('delivery_admission_time')}
                </label>
                {isEditing ? (
                  <input
                    type="time"
                    value={data.admission_time}
                    onChange={(e) => set('admission_time', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                ) : (
                  <p className="text-sm text-title-page-color-100 dark:text-white">
                    {data.admission_time || '—'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-title-page-color-100 dark:text-gray-300">
                {tP('delivery_referred')}
              </span>
              {isEditing ? (
                <input
                  type="checkbox"
                  checked={data.referred}
                  onChange={(e) => set('referred', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
              ) : (
                <span className="text-sm font-medium text-title-page-color-100 dark:text-white">
                  {data.referred ? tP('yes') : tP('no')}
                </span>
              )}
            </div>

            {data.referred && (
              <div className="space-y-3 pl-2 border-l-2 border-gray-200 dark:border-gray-600">
                <div>
                  <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                    {tP('delivery_referring_facility')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={data.referring_facility || ''}
                      onChange={(e) =>
                        set('referring_facility', e.target.value || null)
                      }
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                    />
                  ) : (
                    <p className="text-sm text-title-page-color-100 dark:text-white">
                      {data.referring_facility || '—'}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                      {tP('delivery_referral_date')}
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={data.referral_date || ''}
                        onChange={(e) =>
                          set('referral_date', e.target.value || null)
                        }
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    ) : (
                      <p className="text-sm text-title-page-color-100 dark:text-white">
                        {data.referral_date || '—'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-title-page-color-300 dark:text-gray-400 uppercase mb-1">
                      {tP('delivery_referral_reason')}
                    </label>
                    {isEditing ? (
                      <textarea
                        value={data.referral_reason || ''}
                        onChange={(e) =>
                          set('referral_reason', e.target.value || null)
                        }
                        rows={2}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    ) : (
                      <p className="text-sm text-title-page-color-100 dark:text-white whitespace-pre-wrap">
                        {data.referral_reason || '—'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isLoading && data && hasWritePermission ? (
          isEditing ? (
            <>
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {tPM('buttons.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving
                  ? tPM('timeline.editModal.saving')
                  : tPM('timeline.editModal.save')}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              {tPM('buttons.edit')}
            </Button>
          )
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default DeliveryFileModal;
